import * as React from "react";
import { useReducedMotion } from "framer-motion";
import { useIsPhone } from "@/hooks/use-phone";
import { SHEET_FALL_MS, SHEET_PHONE_RISE_MS, SHEET_RISE_MS } from "@/components/ui/sheetMotion";

/**
 * Cuánto dura la ENTRADA de una hoja, en milisegundos.
 *
 * Los dos números salen de la animación real, no de una estimación:
 *
 * - Teléfono (280 ms): es lo que dura `SHEET_PHONE_RISE`
 *   (`src/components/ui/sheetMotion.ts`), la subida de las superficies con
 *   framer-motion. Las hojas de Radix suben en 260 ms, así que 280 las cubre a
 *   las dos y sobra el fotograma de margen que hace falta para no llegar justo.
 * - Escritorio (260 ms): allí la hoja es una ventana centrada que hace zoom, y
 *   su duración la fija `src/index.css` para TODO `[role="dialog"]`.
 *
 * OJO con `SHEET_ANIM_RISE` (`src/components/ui/sheetChrome.tsx`): escribe
 * `data-[state=open]:duration-500`, pero ese medio segundo NO es el que se ve.
 * `src/index.css` lo pisa con `html [role="dialog"][data-state="open"] {
 * animation-duration: 260ms }`, a propósito fuera de `@layer` para ganarle a la
 * utilidad de Tailwind sin pelear por el orden del archivo. Quien copie el 500
 * de la constante esperará casi el doble de lo que dura la animación y la hoja
 * se quedará en gris con todo listo detrás.
 *
 * Y al revés, tampoco vale quedarse corto: la única espera que ya existía en la
 * aplicación (`TeamMemberDetailDrawer`, un `setTimeout` de 220 ms escrito a
 * mano) se quedó por debajo de los 260 reales, así que el re-render seguía
 * cayendo dentro de la subida — que es justo lo que se quería evitar.
 */
export const SHEET_SETTLE_MS_PHONE = SHEET_PHONE_RISE_MS;
export const SHEET_SETTLE_MS_DESKTOP = SHEET_RISE_MS;

/**
 * Cuánto dura la SALIDA de una hoja, en milisegundos.
 *
 * Sale de `src/index.css`: `html [role="dialog"][data-state="closed"] {
 * animation-duration: 200ms }`. Es el rato que Radix mantiene el panel montado
 * después de que `open` pase a `false`, y por eso importa aquí: durante esos
 * 200 ms la hoja SIGUE VIÉNDOSE mientras baja.
 */
export const SHEET_EXIT_MS = SHEET_FALL_MS;

/**
 * ¿Ha terminado ya de subir la hoja?
 *
 * Devuelve `false` mientras la hoja está en movimiento y `true` en cuanto se ha
 * posado. Sirve para decidir CUÁNDO se pinta el contenido pesado: mientras es
 * `false` la hoja enseña un esqueleto (`SheetBodySkeleton`), y cuando pasa a
 * `true` entra la ficha de verdad, ya con la hoja quieta.
 *
 * LA DECISIÓN CLAVE, que es lo que hay que entender antes de tocar esto: esto NO
 * retrasa la PETICIÓN de datos, solo el momento de PINTARLOS. La consulta sale en
 * cuanto se abre la hoja, como siempre; lo único que se mueve es el instante en
 * el que su resultado se convierte en nodos del DOM.
 *
 * Importa la diferencia porque son los dos únicos repartos posibles y el otro es
 * peor por los dos lados:
 *
 * - Si se retrasa la petición (que es lo que hacía el `setTimeout` de
 *   `TeamMemberDetailDrawer`), se pierden esos ~280 ms de red enteros: la hoja
 *   está quieta y esperando cuando podría llevar media respuesta descargada. El
 *   contenido tarda en aparecer la animación MÁS la consulta, sumadas.
 * - Retrasando solo el pintado, la consulta y la animación corren a la vez y lo
 *   que se ve es lo que tarde la más lenta de las dos, no la suma. En una
 *   consulta rápida el contenido ya está esperando cuando la hoja se posa y
 *   entra en el primer fotograma quieto; en una lenta no se ha perdido nada,
 *   porque salió igual de pronto.
 *
 * Lo que se evita es que el re-render gordo —una hoja como la del sector puede
 * disparar del orden de dieciocho consultas de una sola pulsación— caiga en
 * mitad de la subida. Ahí el navegador tiene que construir y medir todo ese
 * árbol en el mismo hilo que está animando la hoja, se salta fotogramas, y eso
 * es exactamente lo que se veía a tirones. Con la hoja parada el mismo trabajo
 * no se nota: no hay ningún fotograma que llegue tarde porque ya no hay
 * animación que estropear.
 *
 * Con «reducir movimiento» del sistema devuelve `true` a la primera: si el
 * sistema operativo ha pedido que no haya recorrido, `index.css` deja la
 * animación en 1 ms y no queda nada que esperar. Sin esta comprobación se
 * enseñaría un esqueleto durante 280 ms justo en el dispositivo donde no hay
 * animación ninguna, que es lo contrario de lo que pide el ajuste.
 */
export function useSheetSettled(open: boolean): boolean {
  const isPhone = useIsPhone();
  // `useReducedMotion` devuelve `boolean | null` (null mientras no lo sabe).
  const reduceMotion = useReducedMotion() === true;
  const [settled, setSettled] = React.useState(false);
  /**
   * Si la hoja estaba abierta en la vuelta anterior. Hace falta para distinguir
   * «acaba de abrirse» de «el efecto se ha vuelto a ejecutar con la hoja ya
   * abierta» (girar el teléfono cambia `isPhone` y lo relanza), que son dos
   * casos con respuestas contrarias: el primero tiene que volver al esqueleto y
   * el segundo no puede.
   */
  const wasOpen = React.useRef(false);

  React.useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;

    if (open) {
      // Cada apertura empieza por el esqueleto, también si es la segunda vez que
      // se abre la misma hoja: si no, el contenido de la ficha ANTERIOR se
      // pintaría durante la subida. Solo al abrir — una reejecución con la hoja
      // ya abierta (girar el teléfono) no puede devolverla al esqueleto.
      if (justOpened) setSettled(false);
      if (reduceMotion) {
        setSettled(true);
        return;
      }
      const id = window.setTimeout(
        () => setSettled(true),
        isPhone ? SHEET_SETTLE_MS_PHONE : SHEET_SETTLE_MS_DESKTOP,
      );
      return () => window.clearTimeout(id);
    }

    /* AL CERRAR SE ESPERA, y esta es la parte que hay que entender antes de
       «simplificarla» a un `setSettled(false)` a secas.

       Cerrar no desmonta nada de inmediato: Radix mantiene el panel montado los
       200 ms que dura la bajada, y los padres de estas hojas siguen pasando el
       mismo elemento (`selectedTask`, `selectedMember`, `selectedCampaign`… solo
       cambian el booleano), así que el `if (!task) return null` tampoco salta.
       Con un reinicio inmediato, lo que el usuario ve BAJAR no es la ficha que
       estaba mirando: son las barras grises del esqueleto, que aparecen de golpe
       al pulsar la X. Es el mismo tirón que este hook existe para quitar, solo
       que al revés.

       Tampoco vale no reiniciar nunca: entonces la siguiente apertura empezaría
       con `settled` en `true` y el contenido entraría en mitad de la subida.
       Así que se reinicia, pero cuando ya no se ve.

       Va aquí y no en cada pantalla a propósito. La alternativa era escribir
       `open && !settled` en cada sitio, y de hecho dos de las siete lo llevaban y
       cinco no —el mismo fallo copiado cinco veces—; además solo tapa la forma
       NEGATIVA: donde la condición es `settled && <contenido>` (el pie de
       clasificación del muestreo) ese guard no arregla nada, porque lo que hay
       que evitar es que `settled` se caiga, no leerlo de otra manera. */
    if (reduceMotion) {
      // Con «reducir movimiento» la bajada dura 1 ms: no hay nada que esperar.
      setSettled(false);
      return;
    }
    const id = window.setTimeout(() => setSettled(false), SHEET_EXIT_MS);
    // Se limpia al reabrir y al desmontar: si la hoja se vuelve a abrir dentro de
    // esos 200 ms, la rama de arriba ya ha puesto el esqueleto y este temporizador
    // sobra.
    return () => window.clearTimeout(id);
  }, [open, isPhone, reduceMotion]);

  /* No hay bucle de renders posible: el efecto solo escribe el estado, y React
     corta en seco cuando el valor nuevo es igual al que ya había. */
  return settled;
}
